// ui.js - UI event handlers and DOM manipulation
import { calculateAnswer, resetState } from './utils.js';
import { generateAdditionProblem } from './addition.js';
import { generateSubtractionProblem } from './subtraction.js';
import { generateMultiplicationProblem } from './multiplication.js';
import { generateDivisionProblem } from './division.js';
import { generateFractionProblem } from './fractions.js';

const currentLocale = window.currentLocale;
const translations = window.worksheetTranslations; //window.i18n[currentLocale];

function createMixedNumberHTML(mixedNumber: any) {
    return `
        <div class="mixed-number">
            <span class="whole-number">${mixedNumber.whole}</span>
            <div class="fraction">
                <div class="fraction-numerator">${mixedNumber.numerator}</div>
                <div class="fraction-line"></div>
                <div class="fraction-denominator">${mixedNumber.denominator}</div>
            </div>
        </div>
    `;
}

// Helper function to create answer HTML
function createMixedNumberAnswerHTML(answer: string | number) {
    // Convert answer to string first
    const answerStr = answer.toString();

    // If it's just a whole number
    if (!answerStr.includes(' ') && !answerStr.includes('/')) {
        return `<div class="whole-number">${answerStr}</div>`;
    }

    // Split into whole and fractional parts if it's a mixed number
    const parts = answerStr.split(' ');
    if (parts.length === 1) {
        // Just a fraction
        const [num, den] = parts[0].split('/');
        return `
                <div class="fraction">
                    <div class="fraction-numerator">${num}</div>
                    <div class="fraction-line"></div>
                    <div class="fraction-denominator">${den}</div>
                </div>
            `;
    } else {
        // Mixed number
        const whole = parts[0];
        const [num, den] = parts[1].split('/');
        return `
                <div class="mixed-number">
                    <span class="whole-number">${whole}</span>
                    <div class="fraction">
                        <div class="fraction-numerator">${num}</div>
                        <div class="fraction-line"></div>
                        <div class="fraction-denominator">${den}</div>
                    </div>
                </div>
            `;
    }
}

export function basicProblemDiv(problem: BasicProblem, index: number, includeAnswer = false) {
    const problemDiv = document.createElement('div');
    problemDiv.className = 'problem';

    const operation = document.querySelector<HTMLInputElement>('input[name="operation"]:checked')?.value;
    const isFraction = operation === 'fractions';

    const calculationClass = isFraction ? 'calculation fraction' : 'calculation';


    let content;

    content = `
            <div class="problem-number">${translations.exercise} ${index + 1}</div>
            <div class="problem-content">
                <div class="${calculationClass}">
                    ${problem.top}<br>
                    <span class="operation-line">${problem.operation}${problem.bottom}</span>
                    ${includeAnswer ? `<div class="answer">${calculateAnswer(problem)}</div>` : ''}
                </div>
            </div>
        `;

    problemDiv.innerHTML = content;
    return problemDiv;
}

export function equivalentFractionProblemDiv(problem: EquivalentFractionProblem, index: number, includeAnswer = false) {
    const problemDiv = document.createElement('div');
    problemDiv.className = 'problem';

    const operation = document.querySelector<HTMLInputElement>('input[name="operation"]:checked')?.value;
    const isFraction = operation === 'fractions';

    const calculationClass = isFraction ? 'calculation fraction' : 'calculation';


    let content;

    content = `
    <div class="problem-number">${translations.exercise} ${index + 1}</div>
    <div class="problem-content">
        <div class="fraction-container">
            <div class="${calculationClass}">
                <div class="fraction-numerator">${problem.top}</div>
                <div class="fraction-line"></div>
                <div class="fraction-denominator">${problem.bottom}</div>
            </div>
            <div class="fraction-equals">=</div>
            ${includeAnswer ? `
                <div class="${calculationClass}">
                    <div class="fraction-numerator">${problem.answer.top}</div>
                    <div class="fraction-line"></div>
                    <div class="fraction-denominator">${problem.answer.bottom}</div>
                </div>
            ` : `
                <div class="fraction-answer">
                    <div class="fraction-line-answer"></div>
                </div>
            `}
        </div>
    </div>
`;


    problemDiv.innerHTML = content;
    return problemDiv;
}

export function mixedMultiplicationProblemDiv(problem: MixedMultiplicationProblem, index: number, includeAnswer = false) {
    const problemDiv = document.createElement('div');
    problemDiv.className = 'problem';

    const operation = document.querySelector<HTMLInputElement>('input[name="operation"]:checked')?.value;
    const isFraction = operation === 'fractions';

    const calculationClass = isFraction ? 'calculation fraction' : 'calculation';


    let content;

    content = `
        <div class="problem-number">${translations.exercise} ${index + 1}</div>
        <div class="problem-content">
            ${includeAnswer ? `
                <div class="mixed-multiplication-answer">
                    ${createMixedNumberAnswerHTML(calculateAnswer(problem))}
                </div>
            ` : `
                <div class="mixed-multiplication">
                    ${createMixedNumberHTML(problem.mixedNumber)}
                    <div class="operation-symbol">${problem.operation}</div>
                    <div class="multiplier">${problem.multiplier}</div>
                </div>
            `}
        </div>
    `;
    problemDiv.innerHTML = content;
    return problemDiv;
}

export class BasicProblem {
    top: string;
    bottom: string;
    operation: string;

    constructor(top: string, bottom: string, operation: string) {
        this.top = top;
        this.bottom = bottom;
        this.operation = operation;
    }

    generateDiv(index: number, includeAnswer = false): HTMLDivElement {
        return basicProblemDiv(this, index, includeAnswer);
    }
}

export class EquivalentFractionProblem {
    top: string;
    bottom: string;
    operation: string;
    answer: {
        top: string;
        bottom: string;
    }

    constructor(top: string, bottom: string, operation: string, answer: { top: string, bottom: string }) {
        this.top = top;
        this.bottom = bottom;
        this.operation = operation;
        this.answer = answer;
    }

    generateDiv(index: number, includeAnswer = false): HTMLDivElement {
        return equivalentFractionProblemDiv(this, index, includeAnswer);
    }
}

export class MixedMultiplicationProblem {
    mixedNumber: {
        whole: number;
        numerator: number;
        denominator: number;
    };
    operation: string;
    multiplier: number;
    type: string;

    constructor(mixedNumber: { whole: number, numerator: number, denominator: number }, operation: string, multiplier: number) {
        this.mixedNumber = mixedNumber;
        this.operation = operation;
        this.multiplier = multiplier;
        this.type = 'mixedMultiplication';
    }

    generateDiv(index: number, includeAnswer = false): HTMLDivElement {
        return mixedMultiplicationProblemDiv(this, index, includeAnswer);
    }
}

const operationToFunctionMap: { [key: string]: () => BasicProblem | EquivalentFractionProblem | MixedMultiplicationProblem } = {
    'addition': generateAdditionProblem,
    'subtraction': generateSubtractionProblem,
    'multiplication': generateMultiplicationProblem,
    'division': generateDivisionProblem,
    'fractions': generateFractionProblem
};
function generateProblem(): BasicProblem | EquivalentFractionProblem | MixedMultiplicationProblem {
    const operation = document.querySelector<HTMLInputElement>('input[name="operation"]:checked')?.value || "addition";

    return operationToFunctionMap[operation]();
}

export function generateWorksheet() {
    const worksheet = document.getElementById('worksheet');
    const worksheetAnswers = document.getElementById('worksheetAnswers');

    if (!worksheet || !worksheetAnswers) {
        console.error('Could not find worksheet elements:', {
            worksheet,
            worksheetAnswers
        });
        return;
    }

    worksheet.innerHTML = '';
    worksheetAnswers.innerHTML = '';
    resetState();

    // Generate all problems first
    const problems: (BasicProblem | MixedMultiplicationProblem | EquivalentFractionProblem)[] = [];
    for (let i = 0; i < 25; i++) {
        problems.push(generateProblem());
    }

    // Create both the worksheet and answer key
    problems.forEach((problem, index) => {
        // Create problem for worksheet
        // const problemDiv = createProblemDiv(problem, index);
        const problemDiv = problem.generateDiv(index);
        worksheet.appendChild(problemDiv);

        // Create problem with answer for answer key
        // const answerDiv = createProblemDiv(problem, index, true);
        const answerDiv = problem.generateDiv(index, true);
        worksheetAnswers.appendChild(answerDiv);
    });
}

export function initializeUI() {
    const optionElements = ['additionOptions', 'subtractionOptions', 'multiplicationOptions', 'divisionOptions', 'fractionsOptions'];
    const radioValueToElementId: { [key: string]: string } = {
        'addition': 'additionOptions',
        'subtraction': 'subtractionOptions',
        'multiplication': 'multiplicationOptions',
        'division': 'divisionOptions',
        'fractions': 'fractionsOptions'
    };

    // Show/hide operation-specific options based on selected operation
    document.querySelectorAll<HTMLInputElement>('input[name="operation"]').forEach(radio => {
        radio.addEventListener('change', function () {
            optionElements.forEach(option => {
                const optionElement = document.getElementById(option);
                if (optionElement) {
                    optionElement.style.display = 'none';
                }
            });

            // Show the selected operation's options
            const selectedElementId = radioValueToElementId[this.value];
            const selectedElement = document.getElementById(selectedElementId);
            if (selectedElement) {
                selectedElement.style.display = 'block';
            }
        });
    });

    // Handle division mode changes
    document.querySelectorAll<HTMLInputElement>('input[name="divisionMode"]').forEach(radio => {
        radio.addEventListener('change', function () {
            const divisionTimesTablesElement = document.getElementById('divisionTimesTablesOptions');
            if (divisionTimesTablesElement) {
                divisionTimesTablesElement.style.display = this.value === 'timesTables' ? 'block' : 'none';
            }
            const divisionLongDivisionElement = document.getElementById('divisionLongDivisionOptions');
            if (divisionLongDivisionElement) {
                divisionLongDivisionElement.style.display = this.value === 'longDivision' ? 'block' : 'none';
            }
        });
    });

    // Handle multiplication mode changes
    document.querySelectorAll<HTMLInputElement>('input[name="multiplicationMode"]').forEach(radio => {
        radio.addEventListener('change', function () {
            const timesTablesElement = document.getElementById('timesTablesOptions');
            if (timesTablesElement) {
                timesTablesElement.style.display = this.value === 'timesTables' ? 'block' : 'none';
            }
            const numberMultiplicationElement = document.getElementById('numberMultiplicationOptions');
            if (numberMultiplicationElement) {
                numberMultiplicationElement.style.display = this.value === 'numberMultiplication' ? 'block' : 'none';
            }
        });
    });

    // Handle fractions type changes
    document.querySelectorAll<HTMLInputElement>('input[name="fractionsType"]').forEach(radio => {
        radio.addEventListener('change', function () {
            const mixedMultiplicationElement = document.getElementById('mixedMultiplicationOptions');
            if (mixedMultiplicationElement) {
                mixedMultiplicationElement.style.display = this.value === 'mixedMultiplication' ? 'block' : 'none';
            }
        });
    });

    // Update answer key visibility when checkbox changes
    const includeAnswersCheckbox = document.getElementById('includeAnswers') as HTMLInputElement;
    const answerKeyElement = document.getElementById('answerKey') as HTMLInputElement;

    if (includeAnswersCheckbox && answerKeyElement) {
        answerKeyElement.className = includeAnswersCheckbox.checked ? 'answer-key show-answers' : 'answer-key';
        includeAnswersCheckbox.addEventListener('change', function () {
            answerKeyElement.className = this.checked ? 'answer-key show-answers' : 'answer-key';
        });
    }

    // Generate initial worksheet
    generateWorksheet();
}